import { useEffect, useMemo, useState } from "react";
import { CssBaseline, Snackbar, ThemeProvider } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router";
import { useTranslation } from "react-i18next";

import { buildTheme } from "./theme";
import { queryClient } from "./queryClient";
import { router } from "./router";
import { bootstrapSession } from "../features/auth/session";

export function App() {
  const { t, i18n } = useTranslation(["common", "errors"]);
  // MUI bakes its component translations into the theme, so it has to be
  // rebuilt when the UI language changes.
  const theme = useMemo(() => buildTheme(i18n.language), [i18n.language]);

  useEffect(() => {
    // Restores the session from the httpOnly refresh cookie. The promise is
    // memoised at module scope, so StrictMode's double-invoke shares one
    // request rather than racing delete-on-use rotation.
    void bootstrapSession();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <SessionExpiredNotice message={t("errors:sessionExpired")} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * The response interceptor cannot reach i18n or MUI, so it announces a forced
 * sign-out as a DOM event and this listens for it.
 */
function SessionExpiredNotice({ message }: { message: string }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onExpired = () => setOpen(true);
    window.addEventListener("procare:session-expired", onExpired);
    return () =>
      window.removeEventListener("procare:session-expired", onExpired);
  }, []);

  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={() => setOpen(false)}
      message={message}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    />
  );
}
